import React from 'react';
import { Avatar, Box, Container, Divider, Grid } from '@mui/material';
import StorefrontIcon from '@mui/icons-material/Storefront';
import { useParams } from 'react-router-dom';
import PageHeader from 'components/common/PageHeader/PageHeader';
import Section from 'components/common/Section/Section';
import SectionSkeleton from 'components/skeletons/SectionSkeleton';
import CustomButton from 'components/buttons/CustomButton/CustomButton';
import ProductInfoItem from 'components/Product/ProductInfoItem/ProductInfoItem';
import CustomText from 'components/common/CustomText/CustomText';
import StoreEditableSection from 'components/Store/StoreDetails/StoreEditableSection';
import LocationMap from 'components/common/LocationMap/LocationMap';
import {
  StoreAddressForm,
  StoreAddressFormValues,
  StoreHoursForm,
  StoreHoursFormValues,
  StoreNamesForm,
  StoreNamesFormValues,
} from 'components/Store/StoreDetails/StoreForms';
import { useGetStoreById } from 'hooks/useStore';
import { dateToString } from 'common/utils';
import { colors } from 'styles/colors';
import useGlobalStyles from 'styles/globalStyles';

// A field whose value opens something - a phone in WhatsApp - carries links in
// place of a value, one per thing it lists.
type Field = {
  label: string;
  value?: string | null;
  links?: { text: string; href: string }[];
};

// A phone as WhatsApp wants it: the country code and the number, digits only,
// without the number's own leading zero.
const whatsappLink = (phoneCode: string, number: string) => {
  const digits = `${String(phoneCode ?? '').replace(/\D/g, '')}${String(number ?? '').replace(/\D/g, '').replace(/^0+/, '')}`;
  return `https://wa.me/${digits}`;
};

// A time column as the clock reads it, without the seconds.
const toClock = (time?: string) => (time ? time.slice(0, 5) : null);

// Label and value pairs two to a row, the way the product page lays them out.
function Fields({ fields }: { fields: Field[] }) {
  return (
    <>
      {fields.map((field, index) => (
        <React.Fragment key={field.label}>
          <Grid size={{ xs: 12, md: 6 }}>
            {field.links?.length ? (
              <Box>
                <CustomText text={field.label} fontSize={15} fontWeight={600} />
                <Box display="flex" flexWrap="wrap" gap={1.5}>
                  {field.links.map((link) => (
                    <a
                      key={link.href}
                      href={link.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ color: colors.link, textDecoration: 'none' }}
                    >
                      <CustomText text={link.text} fontSize={15} fontWeight={500} color={colors.link} />
                    </a>
                  ))}
                </Box>
              </Box>
            ) : (
              <ProductInfoItem label={field.label} value={field.value} />
            )}
          </Grid>

          {index % 2 === 1 && index < fields.length - 1 && (
            <Grid size={12}>
              <Divider />
            </Grid>
          )}
        </React.Fragment>
      ))}
    </>
  );
}

export default function StoreDetailsPage() {
  const globalStyles = useGlobalStyles();
  const storeId = String(useParams().storeId);

  const { data: store, refetch } = useGetStoreById(storeId, {
    select: (res: any) => res.data,
  }) as { data: any; refetch: () => void };

  if (!store) {
    return (
      <Box className={globalStyles.container} p={3}>
        <SectionSkeleton count={6} />
        <Box mt={3}>
          <SectionSkeleton count={4} />
        </Box>
      </Box>
    );
  }

  const owner = store.owner;
  const address = store.address;
  const location = store.location;

  const storeFields: Field[] = [
    { label: 'English Name', value: store.names?.en },
    { label: 'Arabic Name', value: store.names?.ar },
    { label: 'Products', value: String(store.productCount ?? 0) },
    { label: 'Created', value: store.createdAt ? dateToString(new Date(store.createdAt.split('T')[0])) : null },
  ];

  const ownerFields: Field[] = [
    { label: 'Name', value: owner?.fullName },
    { label: 'Email', value: owner?.emails?.map((e: any) => e.email).join(', ') },
    {
      label: 'Phone',
      value: null,
      links: owner?.phones?.map((p: any) => ({
        text: `+${p.phoneCode} ${p.number}`,
        href: whatsappLink(p.phoneCode, p.number),
      })),
    },
  ];

  const addressFields: Field[] = [
    { label: 'Governorate/Region', value: address?.governorate?.name },
    { label: 'City', value: address?.city?.name },
    { label: 'Address (English)', value: address?.fullAddressEN },
    { label: 'Address (Arabic)', value: address?.fullAddressAR },
    { label: 'Coordinates', value: location ? `${location.latitude}, ${location.longitude}` : null },
  ];

  const hoursFields: Field[] = [
    { label: 'Working Days', value: store.workingDays?.join(', ') },
    {
      label: 'Hours',
      value: store.openingTime ? `${toClock(store.openingTime)} - ${toClock(store.closingTime)}` : null,
    },
  ];

  return (
    <Container
      maxWidth={'xl'}
      className={globalStyles.container}
    >
      <Box pt={2} display="flex" alignItems="center" gap={2}>
        <Avatar
          src={store.avatar?.medium ?? undefined}
          sx={{ width: 40, height: 40, borderRadius: '6px', bgcolor: colors.avatarBg, flexShrink: 0 }}
        >
          <StorefrontIcon sx={{ fontSize: 22, color: colors.primary }} />
        </Avatar>
        <Box flex={1} minWidth={0}>
          <PageHeader
            title={store.info?.name ?? 'Store'}
            buttons={[
              <CustomButton
                key="orders"
                text="Orders"
                variant="outlined"
                height={40}
                onClick={() => window.open(`/stores/store-orders?storeId=${storeId}`, '_blank', 'noopener,noreferrer')}
              />,
              <CustomButton
                key="products"
                text="Products"
                height={40}
                onClick={() => window.open(`/stores/all-stores/${storeId}/products`, '_blank', 'noopener,noreferrer')}
              />,
            ]}
          />
        </Box>
      </Box>

      <Box mt={3}>
        <StoreEditableSection<StoreNamesFormValues>
          title="Store"
          storeId={storeId}
          view={<Fields fields={storeFields} />}
          form={<StoreNamesForm />}
          values={{ en: store.names?.en ?? '', ar: store.names?.ar ?? '' }}
          toPayload={(values) => ({ names: { en: values.en.trim(), ar: values.ar.trim() } })}
          onSaved={refetch}
        />
      </Box>

      <Box mt={3}>
        <Section title="Owner" content={<Fields fields={ownerFields} />} />
      </Box>

      <Box mt={3}>
        <StoreEditableSection<StoreAddressFormValues>
          title="Address"
          storeId={storeId}
          view={
            <>
              <Fields fields={addressFields} />
              {location && (
                <>
                  <Grid size={12}>
                    <Divider />
                  </Grid>
                  <Grid size={12}>
                    <LocationMap latitude={Number(location.latitude)} longitude={Number(location.longitude)} />
                  </Grid>
                </>
              )}
            </>
          }
          form={<StoreAddressForm />}
          values={{
            governorateId: address?.governorate?.id ?? '',
            cityId: address?.city?.id ?? '',
            fullAddressEN: address?.fullAddressEN ?? '',
            fullAddressAR: address?.fullAddressAR ?? '',
            latitude: location?.latitude != null ? String(location.latitude) : '',
            longitude: location?.longitude != null ? String(location.longitude) : '',
          }}
          toPayload={(values) => ({
            address: {
              governorateId: values.governorateId,
              cityId: values.cityId,
              fullAddressAR: values.fullAddressAR.trim(),
              fullAddressEN: values.fullAddressEN.trim() || undefined,
            },
            // Coordinates are sent only as a pair; a half-filled pair is left alone.
            ...(values.latitude !== '' && values.longitude !== ''
              ? { location: { latitude: Number(values.latitude), longitude: Number(values.longitude) } }
              : {}),
          })}
          onSaved={refetch}
        />
      </Box>

      <Box mt={3} mb={3}>
        <StoreEditableSection<StoreHoursFormValues>
          title="Working Hours"
          storeId={storeId}
          view={<Fields fields={hoursFields} />}
          form={<StoreHoursForm />}
          values={{
            workingDays: store.workingDays ?? [],
            openingTime: toClock(store.openingTime) ?? '',
            closingTime: toClock(store.closingTime) ?? '',
          }}
          toPayload={(values) => ({
            workingDays: values.workingDays,
            openingTime: values.openingTime,
            closingTime: values.closingTime,
          })}
          onSaved={refetch}
        />
      </Box>
    </Container>
  );
}
